import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo } from 'react';
import {
	ActivityIndicator, Image, Modal as RNModal, Platform, Pressable, StyleSheet, View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAttachmentImage } from '@/components/attachments/use-attachment-image';
import { Text } from '@/components/ui';
import { useAttachments } from '@/hooks/use-data';
import { type Attachment, AttachmentType } from '@/types/models';
import { selectPrintablePhotos } from '@/utils/work-order-print-photos';

/**
 * The before and after photos, for the Review popup on a To Do approval row.
 *
 * The photos are the evidence the work was done, which is what a property manager is
 * signing off. Without them the popup asked for an approval on the strength of prose alone
 * and the PM had to open the work order anyway, which defeats reviewing in place.
 *
 * Selection reuses the print appendix's rule (#498): images only, before then after,
 * oldest first within each — the order the job happened.
 *
 * ── LAYOUT ──
 *
 * One of each sits SIDE BY SIDE: that is a direct comparison and reads best as a pair. Any
 * more and the two sides STACK — Before above After — each a wrapping grid of thumbnails
 * sized from the popup's width, so every photo is visible and large enough to judge. (A
 * sideways-scrolling strip per side hid most of them and shrank what it showed.) Every
 * thumbnail is a button that opens the photo full size.
 */
const PHOTO_TYPES = [AttachmentType.BeforePhoto, AttachmentType.AfterPhoto] as const;
type PhotoType = (typeof PHOTO_TYPES)[number];
const LABEL: Record<PhotoType, string> = {
	[AttachmentType.BeforePhoto]: 'Before',
	[AttachmentType.AfterPhoto]: 'After',
};
const GAP = 8;
const COLUMN_GAP = 12;
/** Thumbnails are 4:3 — what phones shoot in landscape and what most job photos are. */
const ASPECT = 3 / 4;

const typeOf = (p: Attachment): PhotoType =>
	((p.attachmentType || p.type) === AttachmentType.BeforePhoto ? AttachmentType.BeforePhoto : AttachmentType.AfterPhoto);

/** A photo opened in the viewer, and the ordered list it pages through (all befores, then all afters). */
export interface OpenPhoto {
	photos: Attachment[];
	index: number;
}

export const ApprovalPhotos: React.FC<{
	workOrderId: string;
	/** Width available inside the popup, which sizes the thumbnails. */
	width: number;
	onOpen: (open: OpenPhoto) => void;
}> = ({ workOrderId, width, onOpen }) => {
	const { data, isLoading, isError } = useAttachments(workOrderId);
	const photos = useMemo(() => selectPrintablePhotos(data?.data ?? [], PHOTO_TYPES), [data]);

	if (isLoading) {
		return (
			<View style={styles.section} testID="approve-confirm-photos-loading">
				<Text style={styles.sectionTitle}>Photos</Text>
				<ActivityIndicator size="small" />
			</View>
		);
	}

	/**
	 * A failed load must not read as "No before photos": that tells a PM about to approve
	 * that the job has no evidence, when the truth is only that we could not fetch it.
	 */
	if (isError) {
		return (
			<View style={styles.section} testID="approve-confirm-photos-error">
				<Text style={styles.sectionTitle}>Photos</Text>
				<Text style={styles.error}>
					Couldn&apos;t load the photos. Open the work order to see them before approving.
				</Text>
			</View>
		);
	}

	const groups = PHOTO_TYPES.map((type) => ({ type, list: photos.filter((p) => typeOf(p) === type) }));
	const sideBySide = groups.every((g) => g.list.length <= 1);
	const columnWidth = sideBySide ? (width - COLUMN_GAP) / 2 : width;
	/** Three across on a desktop-width popup, two on a phone. */
	const perRow = sideBySide ? 1 : (width >= 480 ? 3 : 2);
	const thumbWidth = Math.floor((columnWidth - GAP * (perRow - 1)) / perRow);

	return (
		<View style={styles.section} testID="approve-confirm-photos">
			<Text style={styles.sectionTitle}>Photos</Text>
			<View
				style={sideBySide ? styles.sideBySide : styles.stacked}
				testID={sideBySide ? 'approve-confirm-photos-side-by-side' : 'approve-confirm-photos-stacked'}
			>
				{groups.map(({ type, list }) => (
					<View
						key={type}
						style={{ width: columnWidth }}
						testID={`approve-confirm-photos-${LABEL[type].toLowerCase()}`}
					>
						<View style={styles.groupHeader}>
							<Text style={styles.groupLabel}>{LABEL[type]}</Text>
							{list.length > 1 ? (
								<Text style={styles.groupCount} testID={`approve-confirm-photos-${LABEL[type].toLowerCase()}-count`}>
									{list.length}
								</Text>
							) : null}
						</View>
						{list.length > 0 ? (
							<View style={styles.grid}>
								{list.map((photo) => (
									<Thumbnail
										key={photo.id}
										photo={photo}
										width={thumbWidth}
										onPress={() => onOpen({ photos, index: photos.indexOf(photo) })}
									/>
								))}
							</View>
						) : (
							<Text style={styles.empty}>No {LABEL[type].toLowerCase()} photos.</Text>
						)}
					</View>
				))}
			</View>
		</View>
	);
};

const Thumbnail: React.FC<{ photo: Attachment; width: number; onPress: () => void }> = ({ photo, width, onPress }) => {
	const { data: uri, loading } = useAttachmentImage(photo);
	return (
		<Pressable
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={`Open ${LABEL[typeOf(photo)].toLowerCase()} photo ${photo.fileName}`}
			style={({ pressed }) => [styles.thumb, { width, height: Math.round(width * ASPECT) }, pressed && styles.pressed]}
			testID={`approve-confirm-photo-${photo.id}`}
		>
			{uri ? (
				<Image source={{ uri }} style={styles.fill} resizeMode="cover" accessibilityLabel={photo.fileName} />
			) : loading ? (
				<ActivityIndicator size="small" />
			) : (
				<Text style={styles.empty}>Unavailable</Text>
			)}
		</Pressable>
	);
};

/**
 * A photo, FULL SCREEN, over the review popup. Pages through every before and after photo in
 * order — Previous/Next, or ← / → on web — and closes with ×, Escape, or the hardware back
 * gesture, back to the review exactly where it was (scroll position included, since the
 * popup underneath never unmounts).
 *
 * A plain react-native `Modal`, not the app's `ui/Modal`: that one is a centred card with
 * padding and a 90% cap, which is exactly what a full-screen photo must not be.
 */
export const ApprovalPhotoViewer: React.FC<{
	open: OpenPhoto | null;
	onChange: (open: OpenPhoto) => void;
	onClose: () => void;
}> = ({ open, onChange, onClose }) => {
	const insets = useSafeAreaInsets();
	const photos = useMemo(() => open?.photos ?? [], [open]);
	const index = open?.index ?? 0;
	const photo = open ? photos[index] : null;
	const { data: uri, loading } = useAttachmentImage(photo);
	const hasPrev = index > 0;
	const hasNext = index < photos.length - 1;

	useEffect(() => {
		if (!open || Platform.OS !== 'web' || typeof window === 'undefined') return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'ArrowLeft' && index > 0) onChange({ photos, index: index - 1 });
			if (e.key === 'ArrowRight' && index < photos.length - 1) onChange({ photos, index: index + 1 });
		};
		/**
		 * Escape closes the PHOTO and nothing else. RN-web's Modal did not deliver it to this
		 * viewer, and the review popup underneath listens for the same key on `document` keyup.
		 * Taking it on `window` in the CAPTURE phase runs first and stops it there, so one press
		 * never closes both.
		 */
		const onEscape = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			e.stopPropagation();
			onClose();
		};
		window.addEventListener('keydown', onKey);
		window.addEventListener('keyup', onEscape, true);
		return () => {
			window.removeEventListener('keydown', onKey);
			window.removeEventListener('keyup', onEscape, true);
		};
	}, [open, index, photos, onChange, onClose]);

	if (!open || !photo) return null;
	const type = typeOf(photo);
	const sameType = photos.filter((p) => typeOf(p) === type);

	return (
		<RNModal
			visible
			animationType="fade"
			onRequestClose={onClose}
			supportedOrientations={['portrait', 'landscape']}
			statusBarTranslucent
		>
			<View style={styles.viewer} testID="approve-confirm-viewer">
				<View style={styles.viewerFrame}>
					{uri ? (
						<Image
							source={{ uri }}
							style={styles.fill}
							resizeMode="contain"
							accessibilityLabel={photo.fileName}
							testID="approve-confirm-viewer-image"
						/>
					) : loading ? (
						<ActivityIndicator size="large" color="#fff" />
					) : (
						<Text style={styles.viewerUnavailable}>Unavailable</Text>
					)}
				</View>

				<View style={[styles.viewerBar, { paddingTop: insets.top + 8 }]}>
					<Text style={styles.viewerPosition} testID="approve-confirm-viewer-position">
						{LABEL[type]} · {sameType.indexOf(photo) + 1} of {sameType.length}
					</Text>
					<Pressable
						onPress={onClose}
						style={styles.viewerClose}
						accessibilityRole="button"
						accessibilityLabel="Close photo"
						testID="approve-confirm-viewer-close"
					>
						<Ionicons name="close" size={26} color="#fff" />
					</Pressable>
				</View>

				{hasPrev ? (
					<Pressable
						onPress={() => onChange({ photos, index: index - 1 })}
						style={[styles.viewerArrow, styles.viewerArrowLeft]}
						accessibilityRole="button"
						accessibilityLabel="Previous photo"
						testID="approve-confirm-viewer-prev"
					>
						<Ionicons name="chevron-back" size={30} color="#fff" />
					</Pressable>
				) : null}
				{hasNext ? (
					<Pressable
						onPress={() => onChange({ photos, index: index + 1 })}
						style={[styles.viewerArrow, styles.viewerArrowRight]}
						accessibilityRole="button"
						accessibilityLabel="Next photo"
						testID="approve-confirm-viewer-next"
					>
						<Ionicons name="chevron-forward" size={30} color="#fff" />
					</Pressable>
				) : null}

				<Text style={[styles.viewerCaption, { paddingBottom: insets.bottom + 12 }]} numberOfLines={1}>
					{photo.fileName}
				</Text>
			</View>
		</RNModal>
	);
};

const styles = StyleSheet.create({
	section: { marginBottom: 12 },
	sectionTitle: {
		fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase',
		color: '#78909c', marginBottom: 4,
	},
	sideBySide: { flexDirection: 'row', gap: COLUMN_GAP },
	stacked: { gap: 12 },
	groupHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
	groupLabel: { fontSize: 12, fontWeight: '600', color: '#37474f' },
	groupCount: {
		fontSize: 11, fontWeight: '700', color: '#546e7a', backgroundColor: '#eceff1',
		paddingHorizontal: 6, borderRadius: 8, overflow: 'hidden',
	},
	grid: { flexDirection: 'row', flexWrap: 'wrap', gap: GAP },
	thumb: {
		borderRadius: 6, overflow: 'hidden',
		backgroundColor: '#eceff1', alignItems: 'center', justifyContent: 'center',
	},
	pressed: { opacity: 0.8 },
	fill: { width: '100%', height: '100%' },
	empty: { fontSize: 13, fontStyle: 'italic', color: '#90a4ae' },
	error: { fontSize: 13, color: '#c62828' },
	viewer: { flex: 1, backgroundColor: '#000' },
	viewerFrame: { ...StyleSheet.absoluteFill, alignItems: 'center', justifyContent: 'center' },
	viewerUnavailable: { fontSize: 15, color: '#b0bec5' },
	viewerBar: {
		position: 'absolute', top: 0, left: 0, right: 0,
		flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
		paddingHorizontal: 16, paddingBottom: 8, backgroundColor: 'rgba(0, 0, 0, 0.45)',
	},
	viewerPosition: { fontSize: 15, fontWeight: '600', color: '#fff' },
	viewerClose: { padding: 6 },
	// Vertically centred, 56px targets: easy to hit with a thumb, clear of the photo's middle.
	viewerArrow: {
		position: 'absolute', top: '50%', marginTop: -28, width: 56, height: 56, borderRadius: 28,
		// A light tint, not black: the black letterbox would swallow a dark button entirely.
		alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.18)',
	},
	viewerArrowLeft: { left: 12 },
	viewerArrowRight: { right: 12 },
	viewerCaption: {
		position: 'absolute', left: 0, right: 0, bottom: 0, textAlign: 'center',
		paddingTop: 8, paddingHorizontal: 16, fontSize: 13, color: '#cfd8dc', backgroundColor: 'rgba(0, 0, 0, 0.45)',
	},
});
